import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Flame, Anchor, Trophy, Shield } from "lucide-react";

const currency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const ROLE_LABEL = {
    partner: "Silent Partner",
    sports_admiral: "Sports Admiral",
    td_commander: "T&D Commander",
    founding_privateer: "Founding Privateer",
    director: "Director",
    field_operative: "Field Operative",
};

function Bar({ value, max, color }) {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
        </div>
    );
}

export default function CommitmentTracker() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/sovereign-star/commitment", { params: { limit: 8 } })
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, []);

    if (!data) {
        return null; // silent — section appears only when there's data to show
    }

    const max = Math.max(1, ...data.rows.map((r) => r.total_committed));

    return (
        <section
            id="commitment-tracker"
            data-testid="commitment-tracker"
            className="relative w-full"
            style={{ background: "linear-gradient(180deg, hsl(151 60% 5%), hsl(0 0% 3%))" }}
        >
            <div className="absolute inset-0 pointer-events-none opacity-25">
                <div className="absolute top-[10%] left-[-10%] w-[35%] h-[40%] rounded-full bg-[hsl(40_60%_45%/0.20)] blur-[120px]" />
                <div className="absolute bottom-[10%] right-[-10%] w-[35%] h-[40%] rounded-full bg-[hsl(25_70%_50%/0.18)] blur-[120px]" />
            </div>

            <div className="relative max-w-5xl mx-auto px-6 py-20">
                {/* Manifesto */}
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 backdrop-blur border border-[hsl(45_85%_68%/0.3)] mb-6">
                        <Shield size={11} className="text-[hsl(45_85%_68%)]" />
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-semibold">
                            Skin in the Game · Commitment Doctrine
                        </span>
                    </div>
                    <blockquote
                        data-testid="commitment-manifesto"
                        className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-white max-w-4xl mx-auto"
                    >
                        Discipline is our <span className="gold-gradient-text italic font-normal">engine.</span>
                        <span className="block mt-1">Investment is our <span className="gold-gradient-text italic font-normal">armor.</span></span>
                        <span
                            className="block mt-3 text-2xl sm:text-3xl lg:text-4xl text-white/70"
                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                        >
                            We don't build platforms for passengers.
                        </span>
                    </blockquote>
                </div>

                {/* Fleet aggregates */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
                    <div className="rounded-2xl bg-white/[0.03] backdrop-blur border border-white/[0.07] p-5">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-2">Fleet committed</div>
                        <div className="font-heading text-3xl gold-gradient-text" data-testid="commitment-fleet-total">
                            {currency(data.fleet.total_committed)}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white/[0.03] backdrop-blur border border-white/[0.07] p-5">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-2">Active admirals</div>
                        <div className="font-heading text-3xl text-white" data-testid="commitment-active">
                            {data.fleet.active_admirals}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white/[0.03] backdrop-blur border border-white/[0.07] p-5 col-span-2 md:col-span-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-2">Lifetime strikes</div>
                        <div className="font-heading text-3xl text-white">{data.fleet.txn_count}</div>
                    </div>
                </div>

                {/* Leaderboard */}
                <div className="rounded-3xl bg-white/[0.02] backdrop-blur border border-white/[0.07]" data-testid="commitment-board">
                    <div className="px-6 py-5 border-b border-white/[0.07] flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <Trophy size={16} className="text-[hsl(45_85%_68%)]" />
                            <h3 className="font-heading text-2xl tracking-tight text-white">Commitment Tracker</h3>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Top {data.rows.length} by capital injected
                        </div>
                    </div>

                    {data.rows.length === 0 ? (
                        <div className="p-10 text-center text-sm text-white/50" data-testid="commitment-empty">
                            No fleet commitments yet. The first strike sets the tone.
                        </div>
                    ) : (
                        <ul>
                            {data.rows.map((r) => (
                                <li
                                    key={r.rank}
                                    data-testid={`commitment-row-${r.rank}`}
                                    className="px-6 py-4 border-b border-white/[0.05] last:border-0 grid grid-cols-12 items-center gap-3"
                                >
                                    <div className="col-span-1 font-heading text-2xl gold-gradient-text">
                                        #{r.rank}
                                    </div>
                                    <div className="col-span-11 sm:col-span-4 min-w-0">
                                        <div className="font-medium text-white truncate">{r.actor}</div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-0.5">
                                            {ROLE_LABEL[r.role] || r.role} · {r.territories} {r.territories === 1 ? "territory" : "territories"}
                                        </div>
                                    </div>
                                    <div className="col-span-12 sm:col-span-5 space-y-2">
                                        <div className="flex items-center gap-3 text-xs text-white/70">
                                            <Flame size={12} className="text-[hsl(25_90%_60%)]" />
                                            <span className="w-16 text-[10px] uppercase tracking-[0.2em] text-white/40">Fuel</span>
                                            <span className="font-mono tabular-nums">{currency(r.fuel_deployed)}</span>
                                        </div>
                                        <Bar value={r.fuel_deployed} max={max} color="hsl(25 90% 60%)" />
                                        <div className="flex items-center gap-3 text-xs text-white/70 mt-2">
                                            <Anchor size={12} className="text-[hsl(45_85%_68%)]" />
                                            <span className="w-16 text-[10px] uppercase tracking-[0.2em] text-white/40">Boarding</span>
                                            <span className="font-mono tabular-nums">{currency(r.boarding_paid)}</span>
                                        </div>
                                        <Bar value={r.boarding_paid} max={max} color="hsl(45 85% 68%)" />
                                    </div>
                                    <div className="col-span-12 sm:col-span-2 text-right">
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Total</div>
                                        <div className="font-heading text-2xl gold-gradient-text mt-1" data-testid={`commitment-total-${r.rank}`}>
                                            {currency(r.total_committed)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <p className="mt-8 text-center text-xs uppercase tracking-[0.3em] text-white/40">
                    Skin in the game is non-negotiable. Every operative on this board has paid the bond.
                </p>
            </div>
        </section>
    );
}
